import React, { useEffect, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import {
  ActionsDashboardRow,
  ActionsCardWrapper,
  ActionsCardTitle,
  ActionsCount,
  ActionItemsList,
  ActionListItem
} from './style'
import SuccessIcon from '@/assets/icons/success-icon'
import {
  getResponseLevelOneAction,
  getResponseLevelTwoAction,
  getResponseLevelThreeAction,
  getMonitoringResponseActionsBySiteResponse
} from 'api/monitoring'

const RedAlertIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="18" height="18" rx="9" fill="#EF4444" />
    <path d="M9 5V10" stroke="white" strokeWidth="2" strokeLinecap="round" />
    <circle cx="9" cy="13" r="1" fill="white" />
  </svg>
)

const ActionsDashboard = ({ response, monitoringId }) => {
  const [level1Map, setLevel1Map] = useState({})
  const [level2Map, setLevel2Map] = useState({})
  const [level3Map, setLevel3Map] = useState({})
  const [actionsData, setActionsData] = useState([])

  const normalize = (res) => {
    if (Array.isArray(res)) return res
    if (Array.isArray(res?.data)) return res.data
    if (Array.isArray(res?.data?.data)) return res.data.data
    return []
  }

  useEffect(() => {
    let isMounted = true
    async function fetchLevels() {
      try {
        const [level1Res, level2Res, level3Res] = await Promise.all([
          getResponseLevelOneAction(),
          getResponseLevelTwoAction(),
          getResponseLevelThreeAction()
        ])
        if (!isMounted) return
        const l1 = normalize(level1Res)
        const l2 = normalize(level2Res)
        const l3 = normalize(level3Res)

        const map1 = {}
        l1.forEach((item) => {
          map1[item.id] = item.name
        })
        const map2 = {}
        l2.forEach((item) => {
          map2[item.id] = item.name
        })
        const map3 = {}
        l3.forEach((item) => {
          map3[item.id] = item.name
        })

        setLevel1Map(map1)
        setLevel2Map(map2)
        setLevel3Map(map3)
      } catch (err) {
        console.error('Error fetching action levels:', err)
      }
    }
    fetchLevels()
    return () => {
      isMounted = false
    }
  }, [])

  useEffect(() => {
    let isMounted = true
    async function fetchActions() {
      if (!response?.id) return
      try {
        const res = await getMonitoringResponseActionsBySiteResponse(response.id, monitoringId)
        if (!isMounted) return
        if (res?.items) {
          setActionsData(res.items)
        } else if (Array.isArray(res)) {
          setActionsData(res)
        }
      } catch (err) {
        console.error('Error fetching response actions:', err)
      }
    }

    if (response?.monitor_response_actions && Array.isArray(response.monitor_response_actions)) {
      setActionsData(response.monitor_response_actions)
    }
    fetchActions()

    return () => {
      isMounted = false
    }
  }, [response?.id, monitoringId])

  const formattedActions = actionsData.map((item) => {
    const l1Name = level1Map[item.level1_action_id] || ''
    const l2Name = level2Map[item.level2_action_id] || ''
    const l3Name = level3Map[item.level3_action_id] || ''

    let label = l1Name
    if (l2Name) label = `${l1Name} / ${l2Name}`
    if (l3Name) label = `${l1Name} / ${l2Name} / ${l3Name}`

    return {
      ...item,
      l1Name,
      l2Name,
      l3Name,
      label
    }
  })

  const underwayActions = formattedActions.filter((a) => a.action_underway === true || a.action_underway === 1)
  const neededActions = formattedActions.filter((a) => a.action_needed === true || a.action_needed === 1)

  return (
    <ActionsDashboardRow>
      <ActionsCardWrapper>
        <ActionsCardTitle>
          <FormattedMessage id="actionsUnderway" defaultMessage="Actions Underway" />
        </ActionsCardTitle>
        <ActionsCount>{underwayActions.length}</ActionsCount>
        <ActionItemsList>
          {underwayActions.map((item, idx) => (
            <ActionListItem key={`underway-${item.id || idx}`}>
              <SuccessIcon />
              <span>{item.label}</span>
            </ActionListItem>
          ))}
          {underwayActions.length === 0 && (
            <div style={{ color: '#9ca3af', fontSize: '14px' }}>
              <FormattedMessage id="noActionsUnderway" defaultMessage="No actions underway" />
            </div>
          )}
        </ActionItemsList>
      </ActionsCardWrapper>

      <ActionsCardWrapper>
        <ActionsCardTitle>
          <FormattedMessage id="actionsNeeded" defaultMessage="Actions needed" />
        </ActionsCardTitle>
        <ActionsCount>{neededActions.length}</ActionsCount>
        <ActionItemsList>
          {neededActions.map((item, idx) => (
            <ActionListItem key={`needed-${item.id || idx}`}>
              <RedAlertIcon />
              <span>{item.label}</span>
            </ActionListItem>
          ))}
          {neededActions.length === 0 && (
            <div style={{ color: '#9ca3af', fontSize: '14px' }}>
              <FormattedMessage id="noActionsNeeded" defaultMessage="No actions needed" />
            </div>
          )}
        </ActionItemsList>
      </ActionsCardWrapper>
    </ActionsDashboardRow>
  )
}

export default ActionsDashboard
